import { store } from '../store';
import { addCompetitorAlert, clearCompetitorAlerts, setCompetitorConnectionStatus } from '../store/slices/competitorSlice';
import { sessionManager } from './sessionManager';
import { LiveReconnectScheduler } from './liveReconnect';
import { config } from '../config';
import { validateAndRefreshToken } from '../utils/tokenUtils';
import { withDecision } from '../utils/participantRoleChange';

interface CompetitorAlert {
  competitor: string;
  matched_text: string;
  confidence: number;
  match_type: string;
  advantages: Array<{ point: string; context?: string }>;
  weaknesses: Array<{ point: string; reason?: string }>;
  talking_points: string;
  category: string;
  detected_at: string;
  timestamp?: string; // Add timestamp field
  speaker: string;
}

class CompetitorWebSocketService {
  private websocket: WebSocket | null = null;
  private currentUrl: string | null = null;
  private baseUrl: string | null = null;
  private readonly reconnect = new LiveReconnectScheduler('competitor', () => this.connect(this.baseUrl || undefined));
  private pingInterval: NodeJS.Timeout | null = null;
  private clientId: string | null = null;
  private roomId: string | null = null;
  private isConnected: boolean = false;

  private setIdentifiers(): void {
    // Get identifiers from session manager for consistency
    const sessionInfo = sessionManager.getSessionInfo();
    this.roomId = sessionInfo.roomId || sessionInfo.sessionId || 'default';
    this.clientId = sessionInfo.clientId || `competitor-client-${Date.now()}`;

    console.log('🎯 Competitor service using consistent IDs:', {
      roomId: this.roomId,
      clientId: this.clientId
    });
  }

  async connect(url?: string): Promise<void> {
    this.reconnect.activate();

    // Guard: Check if already connected to prevent duplicate connections
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('🎯 Already connected to competitor service, skipping duplicate connection');
      return;
    }

    // Clean up any existing connection before creating new one
    if (this.websocket) {
      console.log('🎯 Closing existing competitor connection before reconnecting');
      this.websocket.close();
      this.websocket = null;
    }

    // The clean base URL (no query), stored before the token fetch so every
    // retry, including one after a failed token fetch, rebuilds the query.
    const baseUrl = url || config.server.competitorUrl;
    this.baseUrl = baseUrl;

    // Always pull a fresh token at (re)connect time. See landmine service for
    // rationale: Redux can lag onIdTokenChanged and reconnects during long
    // sessions are exactly when a stale token would surface.
    const { token } = await validateAndRefreshToken();

    // disconnect() ran while the token was being fetched: open nothing.
    if (!this.reconnect.isActive()) return;

    if (!token) {
      console.error('❌ No JWT token available for competitor service connection');
      store.dispatch(setCompetitorConnectionStatus(false));
      // No socket was opened, so no close event will retry: retry from here.
      this.reconnect.scheduleReconnect();
      throw new Error('No authentication token available');
    }

    return new Promise((resolve, reject) => {
      try {
        this.setIdentifiers();

        // Add token and room_id to URL for authentication and room association
        const separator = baseUrl.includes('?') ? '&' : '?';
        this.currentUrl = `${baseUrl}${separator}token=${encodeURIComponent(token)}&room_id=${encodeURIComponent(this.roomId!)}`;

        console.log(`🎯 Connecting to competitor detection service at: ${baseUrl} with room: ${this.roomId}`);

        // Connect with authentication
        const socket = new WebSocket(this.currentUrl!);
        this.websocket = socket;

        socket.onopen = () => {
          if (this.websocket !== socket) return;
          console.log('🎯 Competitor WebSocket connected');
          this.isConnected = true;
          this.reconnect.markConnected();
          store.dispatch(setCompetitorConnectionStatus(true));

          // Send initial identification message with room_id after connection
          setTimeout(() => {
            this.sendInitialIdentification();
          }, 100); // Small delay to ensure connection is fully established

          // Set up ping interval
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
          }
          this.pingInterval = setInterval(() => {
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
              this.send({ type: 'ping' });
            }
          }, 30000); // Ping every 30 seconds

          resolve();
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('❌ Error parsing competitor WebSocket message:', error);
          }
        };

        socket.onclose = (event) => {
          // A socket this service replaced or closed on purpose reports nothing.
          if (this.websocket !== socket) return;
          this.websocket = null;
          console.log('🎯 Competitor WebSocket connection closed:', event.code, event.reason);
          this.isConnected = false;
          store.dispatch(setCompetitorConnectionStatus(false));

          // Clear ping interval
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
          }

          // Any drop while the session is active (including a server-side
          // 1000 close) is retried: connect() fetches a fresh token from the
          // clean base URL and re-sends join_room. Nothing missed is replayed.
          this.reconnect.scheduleReconnect();
        };

        socket.onerror = (error) => {
          console.error('🎯 Competitor WebSocket error:', error);
          reject(new Error('Competitor WebSocket connection failed'));
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  private handleMessage(data: any) {
    // Check if message is for the currently active room (skip for room_joined to allow updating roomId)
    const messageRoomId = data.room_id || data.data?.room_id;
    if (data.type !== 'room_joined' && messageRoomId && this.roomId && messageRoomId !== this.roomId) {
      console.log(`⏭️ Ignoring competitor message for different room: ${messageRoomId} (active: ${this.roomId})`);
      return;
    }

    switch (data.type) {
      case 'connection_established':
        console.log('🎯 Competitor connection established:', data.message);
        break;

      case 'room_joined':
        console.log(`🎯 Successfully joined competitor detection room: ${data.room_id || data.data?.room_id}`);
        this.roomId = data.room_id || data.data?.room_id || this.roomId;
        break;

      case 'competitor_alert':
        console.log('🎯 Competitor detected:', data.data);
        const alert: CompetitorAlert = data.data;

        // Add proper timestamp if not present
        if (!alert.detected_at) {
          alert.detected_at = new Date().toISOString();
        }

        // The frame's timestamp is the raising line's (participant_lines_moved
        // matches cards on it); detected_at stands in only when it is missing.
        alert.timestamp = alert.timestamp || alert.detected_at;

        // A raiser the owner has since reclassified is labelled as decided
        // (the frame left the detector before the decision reached it).
        const decided = withDecision(alert, store.getState().conversation.participantDecisions ?? {});

        // Add to Redux store using addCompetitorAlert which accumulates alerts
        store.dispatch(addCompetitorAlert(decided));
        console.log('🎯 Competitor alert added to Redux:', decided);
        break;

      case 'stats':
        console.log('🎯 Competitor service stats:', data.data);
        break;

      case 'pong':
        // Heartbeat response
        break;

      case 'error':
        console.error('🎯 Competitor service error:', data.message);
        break;

      default:
        console.log('🎯 Unknown competitor message type:', data.type);
    }
  }

  private sendInitialIdentification(): void {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('🎯 Cannot send identification - WebSocket not connected');
      return;
    }

    // Get latest room_id from sessionManager
    const sessionInfo = sessionManager.getSessionInfo();
    const currentRoomId = sessionInfo.roomId || this.roomId || 'default';

    // Update local roomId
    this.roomId = currentRoomId;

    // Send join_room message to properly join the room (same as landmine service)
    const joinRoomMessage = {
      type: 'join_room',
      room_id: currentRoomId,
    };

    console.log('🎯 Sending competitor join_room message:', joinRoomMessage);
    this.websocket.send(JSON.stringify(joinRoomMessage));
  }

  sendTranscript(text: string, speaker: string = 'unknown', timestamp?: string): boolean {
    // Process both customer and salesperson transcripts for competitor detection
    if (speaker !== 'customer' && speaker !== 'salesperson') {
      console.log(`🎯 Skipping transcript from unknown speaker: ${speaker}`);
      return false;
    }

    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('🎯 Cannot send transcript - not connected to competitor detection service', {
        websocket: !!this.websocket,
        readyState: this.websocket?.readyState,
        isConnected: this.isConnected
      });
      return false;
    }

    const message = {
      type: 'transcript',
      data: {
        text,
        speaker,
        timestamp: timestamp || new Date().toISOString(), // Include timestamp for consistent timeline
      },
      room_id: this.roomId
    };

    try {
      this.websocket.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('🎯 Error sending transcript to competitor service:', error);
      return false;
    }
  }

  send(data: any): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(data));
    }
  }

  joinRoom(roomId: string): void {
    this.roomId = roomId;

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        type: 'join_room',
        room_id: roomId
      };
      this.send(message);
    }
  }

  getStats(): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.send({ type: 'get_stats' });
    }
  }

  clearAlerts(): void {
    store.dispatch(clearCompetitorAlerts());
  }

  disconnect(): void {
    this.reconnect.stop();

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.websocket) {
      this.websocket.close(1000, 'Client disconnect');
      this.websocket = null;
    }

    this.isConnected = false;
    store.dispatch(setCompetitorConnectionStatus(false));
  }

  isServiceConnected(): boolean {
    return this.isConnected;
  }
}

export const competitorWebSocketService = new CompetitorWebSocketService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).competitorWebSocketService = competitorWebSocketService;
}